import { List as ImmutableList } from 'immutable';
import { useState } from 'react';
import { Box } from 'foundations-components/transitional/components/Box';
import { isAttributionReport, isUnifiedAnalyticsReport } from 'reporting-data/tsTypes/reportTypes';
import SingleReportViewerCheckboxTableViz from 'reporting-enablement/components/singleReportViewer/common/checkbox-table/SingleReportViewerCheckboxTableViz';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { H5 } from 'foundations-components/transitional/components/H5';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { getDataType, getDimensions, isTimeSeriesReport } from 'reporting-data/report/configReportGetters';
import useFetchDropdownOptionsForDimensionSelector from '../common/checkbox-table/useFetchDropdownOptionsForDimensionSelector';
import SRVCheckboxTableReportCardActions from './SRVCheckboxTableReportCardActions';
import { SRVSummarizedTableTimeSeriesReport } from './srv-table/SRVSummarizedTableTimeSeriesReport';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useShowAggregationTable = report => {
  const reportDimensions = getDimensions(report);
  const reportDataType = getDataType(report);
  // UA reports should always show the 1D aggregation/checkbox table as that is used to filter down
  // the results, regardless of whether the report is time series or not. This means we still need to support
  // the dimension selector to choose which non-time dimension is selected.
  const is2DSupported = !isUnifiedAnalyticsReport(report);
  const {
    isLoading,
    dropdownOptions
  } = useFetchDropdownOptionsForDimensionSelector(reportDimensions || ImmutableList([]), reportDataType, is2DSupported // Don't fetch the dimension options if using the 2D table
  );
  if (is2DSupported) {
    // We are not using the dimension selector for 2D tables so we don't care if there are any options for it
    return {
      is2DSupported: true,
      isLoading: false,
      showAggregationTable: !isTimeSeriesReport(report)
    };
  }
  return {
    is2DSupported: false,
    isLoading,
    showAggregationTable: dropdownOptions.length > 0
  };
};
export const FullPageSRVCheckboxTable = ({
  previewReport,
  onPreviewReportChange,
  refreshCount,
  reportErrorRenderer,
  initialRowLimit,
  suppressActionsBar
}) => {
  const {
    showAggregationTable,
    isLoading,
    is2DSupported
  } = useShowAggregationTable(previewReport);
  const [checkboxTableReport, setCheckboxTableReport] = useState();
  const [selectedDimension, setSelectedDimension] = useState();
  if (isLoading) {
    return /*#__PURE__*/_jsx(TFlex, {
      justify: "center",
      marginBlock: "600",
      children: /*#__PURE__*/_jsx(LoadingSpinner, {
        "data-test-id": "full-page-srv-checkbox-table-loading"
      })
    });
  }
  if (showAggregationTable) {
    return /*#__PURE__*/_jsxs("div", {
      "data-test-id": "full-page-srv-checkbox-table",
      children: [/*#__PURE__*/_jsx(Box, {
        shrink: 0,
        children: /*#__PURE__*/_jsx(SRVCheckboxTableReportCardActions, {
          previewReport: previewReport,
          onPreviewReportChange: onPreviewReportChange,
          checkboxTableReport: checkboxTableReport,
          selectedDimension: selectedDimension,
          setSelectedDimension: setSelectedDimension,
          is2DSupported: is2DSupported,
          suppressActionsBar: suppressActionsBar
        })
      }), /*#__PURE__*/_jsx(SingleReportViewerCheckboxTableViz, {
        previewReport: previewReport,
        onPreviewReportChange: onPreviewReportChange,
        refreshCount: refreshCount,
        reportErrorRenderer: reportErrorRenderer,
        selectedDimension: selectedDimension,
        onGenerateCheckboxTableReport: setCheckboxTableReport,
        initialRowLimit: initialRowLimit,
        is2DSupported: is2DSupported
      })]
    });
  }
  if (isTimeSeriesReport(previewReport) && !isAttributionReport(previewReport)) {
    // Fall back to a basic non-interactive table report for time-series reports
    return /*#__PURE__*/_jsx(SRVSummarizedTableTimeSeriesReport, {
      previewReport: previewReport,
      onPreviewReportChange: onPreviewReportChange,
      suppressActionsBar: suppressActionsBar
    });
  }
  return /*#__PURE__*/_jsx(TFlex, {
    justify: "center",
    marginBlockStart: "1000",
    children: /*#__PURE__*/_jsx(H5, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-enablement.singleReportViewer.tables.summarizedDisabled"
      })
    })
  });
};